import React, { useState, useMemo } from "react";
import "./CollapsibleGrid.css";

function CollapsibleGrid({
    data,
    columns,
    columnstranslate,
    actions,
    renderExpandedContent,
    expandMode = "single",
    uniquieid = "id",
    pageSizeOptions = [5, 10, 20, 50], // 🔹 custom page size
    onActionClick=null   // ✅ NEW optional callback

}) {
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(pageSizeOptions[0]);

    // 🔹 Sorting logic
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key] ?? "";
            const bValue = b[sortConfig.key] ?? "";

            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [data, sortConfig]);

    // 🔹 Pagination logic
    const totalPages = Math.ceil((sortedData?.length || 0) / pageSize);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return sortedData.slice(start, start + pageSize);
    }, [sortedData, currentPage, pageSize]);

    const toggleRow = (rowId) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
            } else {
                if (expandMode === "single") {
                    newSet.clear();
                }
                newSet.add(rowId);
            }
            return newSet;
        });
    };

    const createIcon = (name) => {
        const icons = {
            'chevron-down': '<polyline points="6,9 12,15 18,9"></polyline>',
            'chevron-right': '<polyline points="9,18 15,12 9,6"></polyline>',
            'eye': '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle>',
            'edit': '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="m18.5 2.5-3 3L22 12l-3 3-3-3-6.5 6.5"></path>',
            'trash': '<polyline points="3,6 5,6 21,6"></polyline><path d="m19,6v14a2 2 0 0 1-2,2H7a2 2 0 0 1-2-2V6m3,0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2,2v2"></path>',
            'clock': '<circle cx="12" cy="12" r="10"></circle><polyline points="12,6 12,12 16,14"></polyline>',
            'users': '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="m23 21v-2a4 4 0 0 0-3-3.87"></path><path d="m16 3.13a4 4 0 0 1 0 7.75"></path>',
            'dollar-sign': '<line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>',
            'calendar-check': `
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                              <path d="M9 15l2 2 4-4"></path>
                            `,
            'calendar-cancel': `
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                          <circle cx="15" cy="17" r="4"></circle>
                          <line x1="13.5" y1="15.5" x2="16.5" y2="18.5"></line>
                          <line x1="16.5" y1="15.5" x2="13.5" y2="18.5"></line>
                        `,
            'sort-asc': `
    <line x1="12" y1="19" x2="12" y2="5"></line>
    <polyline points="5 12 12 5 19 12"></polyline>
  `,
            'sort-desc': `
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <polyline points="19 12 12 19 5 12"></polyline>
  `,
            'sort': `
  <line x1="12" y1="4" x2="12" y2="20"></line>
  <polyline points="8 8 12 4 16 8"></polyline>
  <polyline points="8 16 12 20 16 16"></polyline>
`



        };
        if (icons[name]) {
            return `<svg class="icon" viewBox="0 0 24 24">${icons[name]}</svg>`;
        }

        // 🔹 fallback → use Remix Icon class
        return `<i class="${name}"></i>`;
    };

    // ✅ Pre-check for actions
    const hasVisibleActions =
        actions?.length > 0 &&
        data?.some((row) =>
            actions.some((action) => (action.visibleIf ? action.visibleIf(row) : true))
        );

    const handleSort = (col) => {
        setSortConfig((prev) => {
            if (prev.key === col) {
                return {
                    key: col,
                    direction: prev.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key: col, direction: "asc" };
        });
        setCurrentPage(1); // reset pagination when sorting
    };

    return (
        <div className="collapsible-grid">
            <table className="grid-table">
                <thead>
                    <tr className="grid-th">
                        {columns.map((col, index) => {
                            const isSorted = sortConfig.key === col;
                            return (
                                <th
                                    key={index}
                                    onClick={() => handleSort(col)}
                                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                                >
                                    {columnstranslate[index]}
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: createIcon(
                                                isSorted
                                                    ? sortConfig.direction === "asc"
                                                        ? "sort-asc"
                                                        : "sort-desc"
                                                    : "sort"
                                            ),
                                        }}
                                        style={{ marginLeft: "6px" }}
                                    />
                                </th>
                            );
                        })}
                        {hasVisibleActions && <th>Detaljer / Handling</th>}
                    </tr>
                </thead>
                <tbody>
                    {paginatedData && paginatedData.length > 0 ? (
                        paginatedData.map((row) => {
                            const isExpanded = expandedRows.has(row[uniquieid]);
                            const visibleActions = hasVisibleActions
                                ? actions.filter((action) =>
                                    action.visibleIf ? action.visibleIf(row) : true
                                )
                                : [];

                            return (
                                <React.Fragment key={row[uniquieid]}>
                                    {/* Main row */}
                                    <tr
                                        className={`grid-main-row ${isExpanded ? "expanded-hover" : ""
                                            }`}
                                    >
                                        {columns.map((col, idx) => (
                                            <td
                                                key={idx}
                                                className={`grid-td justify-center ${idx === 0 ? "hover-mouse" : ""
                                                    }`}
                                                onClick={() => {
                                                    if (idx === 0) {
                                                        toggleRow(row[uniquieid]);
                                                    }
                                                }}
                                            >
                                                <div className="dflex">
                                                    {row[col] != null ? (
                                                        <span
                                                            dangerouslySetInnerHTML={{ __html: row[col] }}
                                                        />
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                    {idx === 0 && (
                                                        <button className="expand-btn border-0 bg-transparent">
                                                            <span
                                                                dangerouslySetInnerHTML={{
                                                                    __html: createIcon(
                                                                        isExpanded
                                                                            ? "chevron-down"
                                                                            : "chevron-right"
                                                                    ),
                                                                }}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        ))}

                                        {/* Actions */}
                                        {visibleActions.length > 0 && (
                                            <td className="grid-td actions-cell">
                                                <div className="actions-container justify-center">
                                                    {visibleActions.map((action, index) => (
                                                        <button
                                                            key={index}
                                                            className={`icon-button ${action.className}`}
                                                            style={{
                                                                ...(action.style ? action.style : {}),
                                                            }}
                                                            onClick={() => {
                                                                // 🔹 Call parent method first (if provided)
                                                                if (typeof onActionClick === "function") {
                                                                    onActionClick(action, row);
                                                                }

                                                                action.onClick(row);
                                                                toggleRow(0);
                                                            }}
                                                        >
                                                            <span
                                                                dangerouslySetInnerHTML={{
                                                                    __html: createIcon(action.icon),
                                                                }}
                                                            />
                                                            {action.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </td>
                                        )}
                                    </tr>

                                    {/* Collapsible content row */}
                                    {isExpanded && renderExpandedContent && (
                                        <tr
                                            className={`grid-expanded-row grid-container ${isExpanded ? "expand" : "collapse"
                                                }`}
                                        >
                                            <td
                                                colSpan={columns.length + (hasVisibleActions ? 1 : 0)}
                                                className="expanded-cell"
                                            >
                                                {renderExpandedContent(row)}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <tr>
                            <td
                                colSpan={columns.length + (hasVisibleActions ? 1 : 0)}
                                className="text-center py-2 text-gray-500"
                            >
                                Ingen data funnet
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* 🔹 Pagination Controls */}
            {sortedData.length > 0 && (
                <div className="pagination-controls dflex justify-between mt-2">
                    <div>
                        <label>Rader per side: </label>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="pagination-buttons">
                        <button
                            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            Forrige
                        </button>
                        <span>
                            {currentPage} / {totalPages}
                        </span>
                        <button
                            onClick={() =>
                                setCurrentPage((p) => Math.min(p + 1, totalPages))
                            }
                            disabled={currentPage === totalPages}
                        >
                            Neste
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CollapsibleGrid;
